import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Article {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
  featured_image_url: string | null;
  is_featured: boolean;
  published_at: string | null;
  status: string;
  category_name: string | null;
  author_name: string | null;
}

async function fetchArticles(
  limit: number,
  featured?: boolean,
  includeContent = false,
): Promise<Article[]> {
  // Single round-trip: embed category + author via PostgREST FK joins.
  // `content` is excluded by default — it can be huge and listing pages
  // never render it. Only the article detail query opts in.
  const baseCols =
    "id, title, slug, excerpt, featured_image_url, is_featured, published_at, status, category:categories(name), author:authors(name)";
  const cols = includeContent ? `${baseCols}, content` : baseCols;

  let query = supabase
    .from("articles")
    .select(cols)
    .eq("status", "published")
    .order("published_at", { ascending: false })
    .limit(limit);

  if (featured) {
    query = query.eq("is_featured", true);
  }

  const { data: articles, error } = await query;
  if (error) throw error;
  if (!articles?.length) return [];

  return articles.map((a: any) => ({
    id: a.id,
    title: a.title,
    slug: a.slug,
    excerpt: a.excerpt,
    content: a.content ?? null,
    featured_image_url: a.featured_image_url,
    is_featured: a.is_featured,
    published_at: a.published_at,
    status: a.status,
    category_name: a.category?.name ?? null,
    author_name: a.author?.name ?? null,
  }));
}

export function useLatestArticles(limit = 10) {
  return useQuery({
    queryKey: ["articles", "latest", limit],
    queryFn: () => fetchArticles(limit),
  });
}

export function useFeaturedArticles(limit = 3) {
  return useQuery({
    queryKey: ["articles", "featured", limit],
    queryFn: () => fetchArticles(limit, true),
  });
}

/**
 * Shared hook for the homepage: fetches 20 articles in a single query.
 * Components slice what they need from the result.
 */
export function useHomepageArticles() {
  return useQuery({
    queryKey: ["articles", "homepage"],
    queryFn: () => fetchArticles(20),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}
